import { SearchStrategy, SearchStrategyProps, SearchResult } from './SearchStrategy';

/**
 * A strategy that combines multiple search strategies and runs them in parallel.
 * This generic combiner can take any number of search strategies and execute them
 * concurrently, then merge their results.
 */
export class CombinedSearchStrategy implements SearchStrategy {
  /**
   * Create a new combined search strategy
   * @param strategies Array of search strategies to combine
   */
  constructor(private strategies: SearchStrategy[]) {
    if (!strategies || strategies.length === 0) {
      throw new Error('CombinedSearchStrategy requires at least one strategy');
    }
  }

  /**
   * Execute all search strategies in parallel and combine their results
   * @param props Search parameters to pass to each strategy
   * @returns Combined search results from all strategies
   */
  async search(props: SearchStrategyProps): Promise<SearchResult> {
    try {
      // Execute all strategies in parallel
      const results = await Promise.all(
        this.strategies.map(strategy => strategy.search(props))
      );
      
      // Combine all search predictions
      const searchPredictions = results.flatMap(result => result.searchPredictions);
      
      // Combine all agent predictions (if any)
      const searchAgentPredictions = results.flatMap(result => result.searchAgentPredictions);
      
      return {
        searchPredictions,
        searchAgentPredictions,
      };
    } catch (error) {
      console.error('[CombinedSearchStrategy] Error:', error);
      return {
        searchPredictions: [],
        searchAgentPredictions: [],
      };
    }
  }
} 